/**
 * PDF Text Embedder
 * Embeds edited text into PDF documents using PDF-lib
 */

/**
 * Maps font family names to PDF-lib StandardFonts
 * Supports standard PDF fonts: Helvetica, TimesRoman, Courier, Symbol, ZapfDingbats
 */
function mapFontFamilyToPdfLib(fontFamily) {
  if (!fontFamily) return 'Helvetica';
  
  const normalized = fontFamily.trim();
  
  // Direct mappings for standard PDF fonts
  const fontMap = {
    // Helvetica family
    'Helvetica': 'Helvetica',
    'Arial': 'Helvetica',
    'ArialMT': 'Helvetica',
    'Verdana': 'Helvetica',
    'Tahoma': 'Helvetica',
    'Trebuchet MS': 'Helvetica',
    'Lucida Grande': 'Helvetica',
    'Lucida Sans Unicode': 'Helvetica',
    'DejaVu Sans': 'Helvetica',
    
    // Times family
    'Times': 'TimesRoman',
    'Times New Roman': 'TimesRoman',
    'Times-Roman': 'TimesRoman',
    'Georgia': 'TimesRoman',
    'Palatino': 'TimesRoman',
    'Palatino Linotype': 'TimesRoman',
    'Book Antiqua': 'TimesRoman',
    'Garamond': 'TimesRoman',
    'Baskerville': 'TimesRoman',
    'DejaVu Serif': 'TimesRoman',
    
    // Courier family (monospace)
    'Courier': 'Courier',
    'Courier New': 'Courier',
    'CourierNew': 'Courier',
    'CourierNewPS': 'Courier',
    'Monaco': 'Courier',
    'Consolas': 'Courier',
    'Menlo': 'Courier',
    'DejaVu Sans Mono': 'Courier',
    'monospace': 'Courier',
    
    // Symbol and ZapfDingbats (special fonts)
    'Symbol': 'Symbol',
    'ZapfDingbats': 'ZapfDingbats',
  };
  
  // Check exact match first
  if (fontMap[normalized]) {
    return fontMap[normalized];
  }
  
  // Check case-insensitive match
  const lowerNormalized = normalized.toLowerCase();
  for (const [key, value] of Object.entries(fontMap)) {
    if (key.toLowerCase() === lowerNormalized) {
      return value;
    }
  }
  
  // Check if font name contains keywords
  if (lowerNormalized.includes('times') || lowerNormalized.includes('serif')) {
    return 'TimesRoman';
  }
  if (lowerNormalized.includes('courier') || lowerNormalized.includes('mono')) {
    return 'Courier';
  }
  if (lowerNormalized.includes('symbol')) {
    return 'Symbol';
  }
  if (lowerNormalized.includes('dingbat') || lowerNormalized.includes('zapf')) {
    return 'ZapfDingbats';
  }
  
  // Default fallback to Helvetica (most common)
  return 'Helvetica';
}

/**
 * Gets font metadata for a font family
 * @param {string} fontFamily - Font family name
 * @returns {Object} Font metadata {pdfLibName, isStandard, fallback}
 */
function getFontMetadata(fontFamily) {
  const pdfLibName = mapFontFamilyToPdfLib(fontFamily);
  const standardFonts = ['Helvetica', 'TimesRoman', 'Courier', 'Symbol', 'ZapfDingbats'];
  const isStandard = standardFonts.includes(pdfLibName);
  
  return {
    original: fontFamily,
    pdfLibName: pdfLibName,
    isStandard: isStandard,
    fallback: pdfLibName !== fontFamily ? pdfLibName : null
  };
}

/**
 * Converts hex color string to RGB object
 * @param {string} hex - Hex color string (e.g., "#ff0000" or "#f00")
 * @returns {Object} RGB object with r, g, b values (0-1)
 */
function hexToRgb(hex) {
  if (!hex || hex === 'transparent') {
    return { r: 0, g: 0, b: 0 }; // Default to black
  }
  
  // Remove # if present
  const cleanHex = hex.replace('#', '');
  
  // Handle 3-digit hex
  if (cleanHex.length === 3) {
    const r = parseInt(cleanHex[0] + cleanHex[0], 16) / 255;
    const g = parseInt(cleanHex[1] + cleanHex[1], 16) / 255;
    const b = parseInt(cleanHex[2] + cleanHex[2], 16) / 255;
    return { r, g, b };
  }
  
  // Handle 6-digit hex
  if (cleanHex.length === 6) {
    const r = parseInt(cleanHex.substring(0, 2), 16) / 255;
    const g = parseInt(cleanHex.substring(2, 4), 16) / 255;
    const b = parseInt(cleanHex.substring(4, 6), 16) / 255;
    return { r, g, b };
  }
  
  // Default to black if invalid
  return { r: 0, g: 0, b: 0 };
}

/**
 * Embeds text edits into a PDF document
 * @param {Uint8Array|ArrayBuffer} pdfBytes - Original PDF bytes
 * @param {Object} textEditsByPage - Object mapping page numbers to arrays of text edits
 * @param {Object} viewportInfo - Object mapping page numbers to viewport info {width, height, scale}
 * @returns {Promise<Uint8Array>} Modified PDF bytes with embedded text
 */
async function embedTextIntoPdf(pdfBytes, textEditsByPage, viewportInfo = {}) {
  if (!window.PDFLib) {
    throw new Error('PDF-lib is not loaded');
  }
  
  const { PDFDocument, StandardFonts, rgb } = window.PDFLib;
  
  // Load the PDF document
  const pdfDoc = await PDFDocument.load(pdfBytes);
  const pageCount = pdfDoc.getPageCount();
  
  // Process each page that has text edits
  for (const [pageNumStr, textEdits] of Object.entries(textEditsByPage)) {
    const pageNum = parseInt(pageNumStr, 10);
    
    // Validate page number
    if (pageNum < 1 || pageNum > pageCount) {
      console.warn(`[pdf_text_embedder] Invalid page number ${pageNum}, skipping`);
      continue;
    }
    
    if (!Array.isArray(textEdits) || textEdits.length === 0) {
      continue;
    }
    
    const page = pdfDoc.getPage(pageNum - 1); // PDF-lib uses 0-based indexing
    const pageSize = page.getSize(); // Size in points (1/72 inch)
    
    // Get viewport info for coordinate conversion
    const viewport = viewportInfo[pageNum] || {};
    const viewportWidth = viewport.width || pageSize.width;
    const viewportHeight = viewport.height || pageSize.height;
    const scale = viewport.scale || 1.0;
    
    // Calculate conversion factors: viewport pixels to PDF points
    // PDF uses points (72 DPI), viewport is in pixels at the given scale
    const pointsPerPixel = 72 / (96 * scale); // Assuming 96 DPI screen, adjusted by scale
    const scaleX = (pageSize.width / viewportWidth) || 1;
    const scaleY = (pageSize.height / viewportHeight) || 1;
    
    // Process each text edit
    for (const edit of textEdits) {
      if (!edit || !edit.text || !edit.edited) {
        continue;
      }
      
      // Map font family to PDF-lib font with fallback handling
      const fontMetadata = getFontMetadata(edit.fontFamily);
      let font;
      
      try {
        // Try to embed the mapped standard font
        font = await pdfDoc.embedFont(StandardFonts[fontMetadata.pdfLibName]);
      } catch (error) {
        console.warn(`[pdf_text_embedder] Failed to embed font ${fontMetadata.pdfLibName}, using Helvetica fallback:`, error);
        // Fallback to Helvetica if font embedding fails
        font = await pdfDoc.embedFont(StandardFonts.Helvetica);
      }
      
      // Convert color
      const textColor = hexToRgb(edit.textColor || '#000000');
      const color = rgb(textColor.r, textColor.g, textColor.b);
      
      // Calculate position
      // PDF coordinates: origin is bottom-left, y increases upward
      // Our coordinates: origin is top-left, y increases downward
      // Convert from viewport pixels to PDF points
      const x = (edit.x || 0) * scaleX;
      const fontSize = (edit.fontSize || 12) * scaleY;
      const textHeight = (edit.height || fontSize) * scaleY;
      const y = pageSize.height - ((edit.y || 0) * scaleY) - textHeight;
      const rawWidth = (edit.width || 0) * scaleX;
      const textWidth = rawWidth;

      const pdfOriginalText = (edit.pdfOriginalText || edit.pdf_original_text || '').toString();
      const originalText = (edit.originalText || edit.original_text || '').toString();
      const longestRef = [pdfOriginalText, originalText, edit.text].reduce((a, b) =>
        ((a || '').length >= (b || '').length ? (a || '') : (b || ''))
      );
      let coverageWidth = rawWidth;
      if (longestRef) {
        if (longestRef.length > (edit.text || '').length) {
          const extraChars = longestRef.length - (edit.text || '').length;
          const charWidthEstimate = fontSize * 0.55;
          const extraWidth = Math.min(extraChars * charWidthEstimate, fontSize * 4);
          coverageWidth = Math.max(coverageWidth, textWidth + extraWidth);
        } else if ((edit.text || '').length > longestRef.length) {
          const extraChars = (edit.text || '').length - longestRef.length;
          const charWidthEstimate = fontSize * 0.55;
          const extraWidth = Math.min(extraChars * charWidthEstimate, fontSize * 6);
          coverageWidth = Math.max(coverageWidth, textWidth + extraWidth);
        }
      }

      const paddingX = 8;
      const paddingY = 5;
      const descenderPadding = Math.round(fontSize * 0.28 * 10) / 10;
      const rectX = x - paddingX;
      const rectY = y - paddingY - descenderPadding;
      const rectWidth = coverageWidth + paddingX * 2;
      const rectHeight = textHeight + paddingY * 2 + descenderPadding;

      // Build text options
      const textOptions = {
        x: x,
        y: y,
        size: fontSize,
        font: font,
        color: color,
      };

      // Handle text alignment
      if (edit.alignment) {
        switch (edit.alignment) {
          case 'center':
            textOptions.x = x + textWidth / 2;
            break;
          case 'right':
            textOptions.x = x + textWidth;
            break;
          case 'justify':
            break;
        }
      }

      try {
        page.drawRectangle({
          x: rectX,
          y: rectY,
          width: rectWidth,
          height: rectHeight,
          color: rgb(1, 1, 1),
        });
        page.drawText(edit.text, textOptions);
        
        // Draw background color if specified and not white/transparent
        if (edit.bgColor && edit.bgColor !== '#ffffff' && edit.bgColor !== 'transparent') {
          const bgColor = hexToRgb(edit.bgColor);
          const bgRgb = rgb(bgColor.r, bgColor.g, bgColor.b);
          
          page.drawRectangle({
            x: x,
            y: y,
            width: (edit.width || 0) * scaleX,
            height: textHeight,
            color: bgRgb,
            opacity: 0.3, // Make background semi-transparent
          });
        }
      } catch (error) {
        console.warn(`[pdf_text_embedder] Failed to embed text edit:`, error, edit);
      }
    }
  }
  
  // Save and return the modified PDF
  return await pdfDoc.save();
}

/**
 * Embeds text edits for a single page into PDF
 * @param {Uint8Array|ArrayBuffer} pdfBytes - Original PDF bytes
 * @param {number} pageNum - Page number (1-based)
 * @param {Array} textEdits - Array of text edits for this page
 * @param {Object} viewportInfo - Viewport info {width, height, scale}
 * @returns {Promise<Uint8Array>} Modified PDF bytes
 */
async function embedTextIntoPdfPage(pdfBytes, pageNum, textEdits, viewportInfo = {}) {
  const textEditsByPage = { [pageNum]: textEdits };
  const viewportInfoByPage = { [pageNum]: viewportInfo };
  return await embedTextIntoPdf(pdfBytes, textEditsByPage, viewportInfoByPage);
}

// Export functions
window.PdfTextEmbedder = {
  embedTextIntoPdf,
  embedTextIntoPdfPage,
  mapFontFamilyToPdfLib,
  getFontMetadata,
  hexToRgb,
};

